import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './Modal.scss';

interface ModalProps {
  open: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  closeOnBackdrop?: boolean;
  disableEsc?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onClose,
  onConfirm,
  closeOnBackdrop = true,
  disableEsc = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || disableEsc) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose, disableEsc]);

  useEffect(() => {
    if (open) firstFocusableRef.current?.focus();
  }, [open]);

  const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onClose();
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropClick}>
      <div className="modal-moderation" role="dialog" aria-modal="true" ref={dialogRef}>
        <div className="modal-moderation__header">
          <h3 className="modal-moderation__title">{title}</h3>
          <button
            type="button"
            className="modal-moderation__close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            ×
          </button>
        </div>

        {description && <div className="modal-moderation__body">{description}</div>}

        <div className="modal-moderation__footer">
          <button
            type="button"
            className="modal-moderation__btn modal-moderation__btn--secondary"
            onClick={onClose}
            disabled={submitting}
            ref={firstFocusableRef}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className="modal-moderation__btn modal-moderation__btn--primary"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Processing…' : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
